import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { LegacyGenreResponse } from '../types';
import { gameService } from '../services/api';
import GameList from '../components/GameList';
import Pagination from '../components/Pagination';
import { GameListSkeleton, PageHeaderSkeleton } from '../components/skeletons';
import { ArrowLeft, Tag } from 'lucide-react';

const GenrePage: React.FC = () => {
  const { genre } = useParams<{ genre: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [genreData, setGenreData] = useState<LegacyGenreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    if (genre) {
      fetchGenreGames(genre, currentPage);
    }
  }, [genre, currentPage]);

  const fetchGenreGames = async (genreName: string, page: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await gameService.getGenreGames(genreName, { page });
      setGenreData(data);
    } catch (err) {
      setError('Erreur lors du chargement des jeux du genre');
      console.error('Genre error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setSearchParams({ page: page.toString() });
    // Scroll smooth vers le haut
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatGenreName = (genreName: string) => {
    return genreName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
        <PageHeaderSkeleton />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <GameListSkeleton count={12} />
        </div>
      </div>
    );
  }

  if (error || !genreData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Erreur
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'Genre non trouvé'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      {/* Header */}
      <div className="bg-white dark:bg-dark-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </button>
            <div className="flex items-center">
              <Tag className="w-6 h-6 text-primary-600 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatGenreName(genre || '')}
              </h1>
            </div>
          </div>
          
          <p className="text-gray-600 dark:text-gray-400">
            {genreData.total_games} jeu{genreData.total_games > 1 ? 'x' : ''} dans cette catégorie
          </p>
        </div>
      </div>

      {/* Games List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <GameList games={genreData.games} error={error} />
        
        {genreData.total_pages > 1 && (
          <div className="mt-8">
            <Pagination
              currentPage={genreData.current_page}
              totalPages={genreData.total_pages}
              hasNext={genreData.has_next}
              hasPrevious={genreData.has_previous}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GenrePage;
